import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../components/Icon';
import PremiumSelect from '../components/PremiumSelect';
import PromoConfirmModal from '../components/PromoConfirmModal';
import PageSizeDropdown from '../components/PageSizeDropdown';
import {
  getMarketplaceServices,
  createMarketplaceService,
  updateMarketplaceService,
  deleteMarketplaceService,
  getMarketplacePurchases,
  updateMarketplacePurchaseStatus,
} from '../api';
import { formatDisplayDate } from '../utils/promoCodeUtils';
import '../css/sort-menu.css';
import '../css/pagination.css';

// Значения enum PurchaseStatus на бэкенде.
const PURCHASE_STATUSES = ['OPEN', 'IN_PROGRESS', 'CLOSED'];

const purchaseStatusBadgeClass = (status) => {
  switch (status) {
    case 'OPEN':
      return 'open';
    case 'IN_PROGRESS':
      return 'in-progress';
    case 'CLOSED':
      return 'closed';
    default:
      return 'open';
  }
};

const purchaseStatusIcon = (status) => {
  switch (status) {
    case 'OPEN':
      return 'clock';
    case 'IN_PROGRESS':
      return 'refresh';
    case 'CLOSED':
      return 'check-circle';
    default:
      return 'clock';
  }
};

// Языки контента сервиса: на бэке это отдельные поля titleRo/titleRu/titleEn и
// descriptionRo/descriptionRu/descriptionEn.
const SERVICE_LANGS = [
  { code: 'ro', suffix: 'Ro' },
  { code: 'ru', suffix: 'Ru' },
  { code: 'en', suffix: 'En' },
];

const DEFAULT_SERVICE_FORM = {
  icon: '',
  titleRo: '',
  titleRu: '',
  titleEn: '',
  descriptionRo: '',
  descriptionRu: '',
  descriptionEn: '',
  price: '',
};

// Суффикс поля для языка интерфейса (i18n.language вида `ru`, `en-US`).
const localeSuffix = (locale) => {
  const l = (locale || '').split('-')[0].toLowerCase();
  if (l === 'ru') return 'Ru';
  if (l === 'en') return 'En';
  return 'Ro';
};

// Значение мультиязычного поля на языке интерфейса с откатом на другие языки.
const localizedField = (obj, field, locale) => {
  if (!obj) return '';
  const order = [localeSuffix(locale), 'Ro', 'Ru', 'En'];
  for (let i = 0; i < order.length; i += 1) {
    const value = obj[`${field}${order[i]}`];
    if (value) return value;
  }
  return obj[field] || '';
};

// Иконка сервиса — строка: ссылка на картинку рендерится как <img>, иначе как
// текст/emoji.
const ServiceIcon = ({ value, size = 22 }) => {
  const str = (value || '').trim();
  const isUrl = /^(https?:\/\/|data:)/i.test(str);
  const boxStyle = { width: size, height: size };
  if (!str) {
    return (
      <span className="admin-mkt-icon admin-mkt-icon--empty" style={boxStyle} aria-hidden="true">
        <Icon name="package" size={Math.round(size * 0.5)} />
      </span>
    );
  }
  if (isUrl) {
    return <img className="admin-mkt-icon" src={str} alt="" style={boxStyle} />;
  }
  return (
    <span className="admin-mkt-icon admin-mkt-icon--text" style={{ ...boxStyle, fontSize: Math.round(size * 0.55) }}>
      {str}
    </span>
  );
};

// Поля сортировки покупок — совпадают с полями сущности на бэке (Spring Pageable).
const PURCHASE_SORT_FIELDS = ['createdAt', 'price', 'status'];
const PURCHASE_SORT_ICONS = { createdAt: 'calendar', price: 'tag', status: 'sliders' };

// Компактное меню сортировки покупок (в стиле сорт-меню заказов/жалоб).
// Выпадашка рендерится в портал к <body> с fixed-позиционированием, потому что
// список маркетплейса лежит в контейнере с `overflow: hidden`, который иначе
// обрезал бы меню.
const PurchasesSortMenu = ({ sort, direction, onSortChange, onDirectionChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  const toggle = () => setOpen((o) => !o);

  // Позиционируем портал-выпадашку в вьюпорте: по умолчанию под кнопкой с
  // выравниванием по её правому краю; если снизу не помещается — раскрываем
  // вверх; по горизонтали прижимаем к границам экрана.
  useLayoutEffect(() => {
    if (!open) return;
    const btn = ref.current;
    const dd = dropdownRef.current;
    if (!btn || !dd) return;
    const rect = btn.getBoundingClientRect();
    const ddW = dd.offsetWidth;
    const ddH = dd.offsetHeight;
    const margin = 8;
    let left = rect.right - ddW;
    left = Math.min(Math.max(margin, left), window.innerWidth - ddW - margin);
    let top = rect.bottom + margin;
    if (top + ddH + margin > window.innerHeight) {
      const above = rect.top - ddH - margin;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - ddH - margin);
    }
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onScrollResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open]);

  const fieldLabel = {
    createdAt: t('admin.marketplace.purchases.sortByDate'),
    price: t('admin.marketplace.purchases.sortByPrice'),
    status: t('admin.marketplace.purchases.sortByStatus'),
  };
  const directionOptions = [
    { value: 'DESC', icon: '↓', label: t('orders.filters.descending') },
    { value: 'ASC', icon: '↑', label: t('orders.filters.ascending') },
  ];
  const isActive = sort !== 'createdAt' || direction !== 'DESC';

  const optionStyle = (active) => ({
    border: '1px solid var(--border-color)',
    background: active ? 'var(--nav-active-bg, rgba(59, 130, 246, 0.05))' : 'var(--bg-secondary)',
    padding: '10px 12px',
    justifyContent: 'flex-start',
  });

  return (
    <div className="sort-menu-wrapper" ref={ref}>
      <button
        type="button"
        className={`sort-menu-button${isActive ? ' filter-active' : ''}`}
        onClick={toggle}
        aria-label={t('admin.marketplace.purchases.sort')}
      >
        <span className="sort-menu-icon">
          <Icon name="sort-arrows" size={14} />
        </span>
        <span className="sort-menu-label">{t('admin.marketplace.purchases.sort')}</span>
        <span className="sort-menu-arrow">
          <Icon name="chevron-down" size={12} className={`expand-icon ${open ? 'open' : ''}`} />
        </span>
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={dropdownRef}
              className="sort-menu-dropdown admin-mkt-sort-dropdown"
              style={{ position: 'fixed', top: pos.top, left: pos.left }}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
            <div className="orders-sort-main-grid" style={{ padding: '0 8px' }}>
              <div className="sort-menu-section" style={{ borderRight: '1px solid var(--border-color)' }}>
                <div className="sort-menu-section-title" style={{ paddingLeft: '4px', marginBottom: '12px' }}>
                  {t('orders.filters.sortBy')}
                </div>
                <div className="sort-menu-options" style={{ gap: '8px' }}>
                  {PURCHASE_SORT_FIELDS.map((field) => (
                    <button
                      key={field}
                      type="button"
                      className={`sort-menu-option ${sort === field ? 'active' : ''}`}
                      onClick={() => onSortChange(field)}
                      style={optionStyle(sort === field)}
                    >
                      <span className="sort-menu-option-icon" style={{ display: 'inline-flex', marginRight: '8px' }}>
                        <Icon name={PURCHASE_SORT_ICONS[field]} size={15} />
                      </span>
                      <span className="sort-menu-option-label" style={{ fontWeight: 600 }}>{fieldLabel[field]}</span>
                      {sort === field && (
                        <span className="sort-menu-option-check">
                          <Icon name="check" size={14} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sort-menu-section">
                <div className="sort-menu-section-title" style={{ paddingLeft: '4px', marginBottom: '12px' }}>
                  {t('orders.filters.sortDirection')}
                </div>
                <div className="sort-menu-options" style={{ gap: '8px' }}>
                  {directionOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                      onClick={() => onDirectionChange(option.value)}
                      style={optionStyle(direction === option.value)}
                    >
                      <span className="sort-menu-option-icon" style={{ fontSize: '18px', marginRight: '4px' }}>
                        {option.icon}
                      </span>
                      <span className="sort-menu-option-label" style={{ fontWeight: 600 }}>{option.label}</span>
                      {direction === option.value && (
                        <span className="sort-menu-option-check">
                          <Icon name="check" size={14} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

// Управление сервисами (CRUD) — только для DEV, эндпоинты /api/dev/marketplace/services.
// Покупки (просмотр + смена статуса) — доступны и ADMIN, эндпоинты /api/admin/marketplace/purchases.
// Для ADMIN без DEV раздел "Сервисы" скрыт, показывается сразу "Покупки".
const AdminMarketplace = ({ isDev = false }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'ru';

  const [section, setSection] = useState(isDev ? 'services' : 'purchases'); // 'services' | 'purchases'

  const statusLabel = useCallback(
    (status) => t(`admin.marketplace.purchaseStatuses.${status}`, status),
    [t]
  );

  // Стабильная ссылка на актуальный t, чтобы загрузчики не пересоздавались.
  const tRef = useRef(t);
  tRef.current = t;

  // ── Сервисы ──
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState(null);

  const [createForm, setCreateForm] = useState(DEFAULT_SERVICE_FORM);
  const [createLang, setCreateLang] = useState('ro');
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editLang, setEditLang] = useState('ro');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const loadServices = useCallback(async () => {
    setServicesLoading(true);
    setServicesError(null);
    try {
      const data = await getMarketplaceServices();
      setServices(Array.isArray(data) ? data : []);
    } catch (e) {
      setServicesError(e?.message || tRef.current('admin.marketplace.services.loadError'));
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  }, []);

  // ── Покупки ──
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [purchasesError, setPurchasesError] = useState(null);
  const [purchasePage, setPurchasePage] = useState(0);
  const [purchasePageSize, setPurchasePageSize] = useState(10);
  const [purchaseTotalPages, setPurchaseTotalPages] = useState(0);
  const [purchaseTotalElements, setPurchaseTotalElements] = useState(0);
  const [purchaseSort, setPurchaseSort] = useState('createdAt');
  const [purchaseDir, setPurchaseDir] = useState('DESC');

  const [statusTarget, setStatusTarget] = useState(null); // покупка
  const [statusValue, setStatusValue] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const loadPurchases = useCallback(async (page, size, sort = 'createdAt', direction = 'DESC') => {
    setPurchasesLoading(true);
    setPurchasesError(null);
    try {
      const data = await getMarketplacePurchases(page, size, sort, direction);
      setPurchases(Array.isArray(data?.content) ? data.content : []);
      setPurchaseTotalPages(Number(data?.totalPages) || 0);
      setPurchaseTotalElements(Number(data?.totalElements) || 0);
    } catch (e) {
      setPurchasesError(e?.message || tRef.current('admin.marketplace.purchases.loadError'));
      setPurchases([]);
      setPurchaseTotalPages(0);
      setPurchaseTotalElements(0);
    } finally {
      setPurchasesLoading(false);
    }
  }, []);

  // Начальная загрузка обоих разделов ровно один раз (гард от двойного маунта
  // React.StrictMode в dev).
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    // Сервисы — DEV-only эндпоинт, для просто ADMIN его не запрашиваем.
    if (isDev) {
      loadServices();
    }
    loadPurchases(0, purchasePageSize, purchaseSort, purchaseDir);
  }, [isDev, loadServices, loadPurchases, purchasePageSize, purchaseSort, purchaseDir]);

  const handlePurchasePageChange = (page) => {
    setPurchasePage(page);
    loadPurchases(page, purchasePageSize, purchaseSort, purchaseDir);
  };

  const handlePurchasePageSizeChange = (size) => {
    const nextSize = Number(size) || 10;
    setPurchasePageSize(nextSize);
    setPurchasePage(0);
    loadPurchases(0, nextSize, purchaseSort, purchaseDir);
  };

  const handlePurchaseSortChange = (field) => {
    if (field === purchaseSort) return;
    setPurchaseSort(field);
    setPurchasePage(0);
    loadPurchases(0, purchasePageSize, field, purchaseDir);
  };

  const handlePurchaseDirChange = (dir) => {
    if (dir === purchaseDir) return;
    setPurchaseDir(dir);
    setPurchasePage(0);
    loadPurchases(0, purchasePageSize, purchaseSort, dir);
  };

  // Пагинация покупок — тот же стандарт, что и в поиске пользователей
  // (номера страниц с многоточием + выпадашка размера страницы).
  const getVisiblePurchasePages = () => {
    const totalPages = purchaseTotalPages || 0;
    const current = purchasePage;
    if (totalPages <= 1) return [];
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);

    const pages = [0];
    const left = Math.max(1, current - 1);
    const right = Math.min(totalPages - 2, current + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p += 1) pages.push(p);
    if (right < totalPages - 2) pages.push('right-ellipsis');
    pages.push(totalPages - 1);
    return pages;
  };

  const renderPurchasesPager = () => {
    if ((purchasesLoading && purchases.length === 0) || purchaseTotalPages <= 0) return null;

    const isFirst = purchasePage <= 0;
    const isLast = purchasePage >= purchaseTotalPages - 1;
    const pages = getVisiblePurchasePages();

    const total = purchaseTotalElements || 0;
    const from = total > 0 ? purchasePage * purchasePageSize + 1 : 0;
    const to = Math.min((purchasePage + 1) * purchasePageSize, total);

    return (
      <div className="orders-pager orders-pager-bottom">
        <div className="orders-pager-info">
          {t('common.pagination.showing', { from, to, total })}
        </div>

        <div className="orders-pager-controls">
          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePurchasePageChange(purchasePage - 1)}
            disabled={isFirst || purchasesLoading}
          >
            {t('common.pagination.previous')}
          </button>

          <div className="orders-pager-pages">
            {pages.map((item) => {
              if (typeof item !== 'number') {
                return <span key={item} className="orders-pager-ellipsis">...</span>;
              }
              const active = item === purchasePage;
              return (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page ${active ? 'active' : ''}`}
                  onClick={() => handlePurchasePageChange(item)}
                  disabled={purchasesLoading}
                  aria-current={active ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePurchasePageChange(purchasePage + 1)}
            disabled={isLast || purchasesLoading}
          >
            {t('common.pagination.next')}
          </button>

          <PageSizeDropdown
            value={purchasePageSize}
            onChange={(newSize) => handlePurchasePageSizeChange(newSize)}
            options={[10, 20, 50, 100]}
            position="bottom"
          />
        </div>
      </div>
    );
  };

  // ── Создание сервиса ──
  const buildServicePayload = (form, setError, setLang) => {
    const icon = form.icon.trim();
    const priceNum = Number(form.price);

    if (!icon) {
      setError(t('admin.marketplace.services.errorIcon'));
      return null;
    }

    // Названия обязательны на всех трёх языках (@NotBlank на бэкенде).
    const missing = SERVICE_LANGS.find((lang) => !(form[`title${lang.suffix}`] || '').trim());
    if (missing) {
      if (setLang) setLang(missing.code);
      setError(
        t('admin.marketplace.services.errorTitleLang', {
          lang: t(`admin.marketplace.services.languages.${missing.code}`),
        })
      );
      return null;
    }

    if (!Number.isInteger(priceNum) || priceNum <= 0) {
      setError(t('admin.marketplace.services.errorPrice'));
      return null;
    }

    const payload = { icon, price: priceNum };
    SERVICE_LANGS.forEach(({ suffix }) => {
      payload[`title${suffix}`] = form[`title${suffix}`].trim();
      payload[`description${suffix}`] = (form[`description${suffix}`] || '').trim() || null;
    });
    return payload;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(false);

    const payload = buildServicePayload(createForm, setCreateError, setCreateLang);
    if (!payload) return;

    setCreateSubmitting(true);
    try {
      await createMarketplaceService(payload);
      setCreateSuccess(true);
      setCreateForm(DEFAULT_SERVICE_FORM);
      setCreateLang('ro');
      setCreateOpen(false);
      await loadServices();
      window.setTimeout(() => setCreateSuccess(false), 5000);
    } catch (err) {
      setCreateError(err?.message || t('admin.marketplace.services.createError'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  // ── Редактирование сервиса ──
  const openEdit = (service) => {
    setEditError(null);
    setEditTarget(service);
    setEditLang('ro');
    const form = {
      icon: service.icon || '',
      price: service.price != null ? String(service.price) : '',
    };
    SERVICE_LANGS.forEach(({ suffix }) => {
      form[`title${suffix}`] = service[`title${suffix}`] || '';
      form[`description${suffix}`] = service[`description${suffix}`] || '';
    });
    setEditForm(form);
  };

  const closeEdit = () => {
    if (editSubmitting) return;
    setEditTarget(null);
    setEditForm(null);
    setEditError(null);
  };

  const handleEditSubmit = async () => {
    if (!editTarget || !editForm) return;
    setEditError(null);

    const payload = buildServicePayload(editForm, setEditError, setEditLang);
    if (!payload) return;

    setEditSubmitting(true);
    try {
      await updateMarketplaceService(editTarget.id, payload);
      await loadServices();
      setEditTarget(null);
      setEditForm(null);
    } catch (err) {
      setEditError(err?.message || t('admin.marketplace.services.updateError'));
    } finally {
      setEditSubmitting(false);
    }
  };

  // ── Удаление сервиса ──
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    setDeleteSubmitting(true);
    try {
      await deleteMarketplaceService(deleteTarget.id);
      await loadServices();
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err?.message || t('admin.marketplace.services.deleteError'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── Смена статуса покупки ──
  const openStatus = (purchase) => {
    setStatusError(null);
    setStatusTarget(purchase);
    setStatusValue(purchase.status);
  };

  const closeStatus = () => {
    if (statusSubmitting) return;
    setStatusTarget(null);
    setStatusValue(null);
    setStatusError(null);
  };

  const handleConfirmStatus = async () => {
    if (!statusTarget || !statusValue) return;
    if (statusValue === statusTarget.status) {
      closeStatus();
      return;
    }
    setStatusError(null);
    setStatusSubmitting(true);
    try {
      const updated = await updateMarketplacePurchaseStatus(statusTarget.id, statusValue);
      // Обновляем строку на месте, без перезагрузки всей страницы.
      setPurchases((prev) =>
        prev.map((p) =>
          p.id === statusTarget.id ? { ...p, status: updated?.status || statusValue } : p
        )
      );
      setStatusTarget(null);
      setStatusValue(null);
    } catch (err) {
      setStatusError(err?.message || t('admin.marketplace.purchases.statusError'));
    } finally {
      setStatusSubmitting(false);
    }
  };

  // Форма сервиса: иконка и цена общие, название/описание — по вкладкам языков
  // (RO / RU / EN), которые на бэке лежат в отдельных полях.
  const renderServiceFields = (form, setForm, disabled, lang, setLang) => {
    const suffix = (SERVICE_LANGS.find((l) => l.code === lang) || SERVICE_LANGS[0]).suffix;
    const titleKey = `title${suffix}`;
    const descKey = `description${suffix}`;

    return (
      <>
        <div className="admin-promo-create-grid admin-mkt-form-grid">
          <label className="admin-promo-field">
            <span className="admin-promo-field-label">{t('admin.marketplace.services.icon')}</span>
            <input
              className="premium-input-minimal"
              value={form.icon}
              onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
              placeholder={t('admin.marketplace.services.iconPlaceholder')}
              disabled={disabled}
              required
            />
            <span className="admin-mkt-hint">{t('admin.marketplace.services.iconHint')}</span>
          </label>
          <label className="admin-promo-field">
            <span className="admin-promo-field-label">{t('admin.marketplace.services.price')}</span>
            <input
              className="premium-input-minimal"
              type="number"
              min="1"
              step="1"
              value={form.price}
              onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
              placeholder={t('admin.marketplace.services.pricePlaceholder')}
              disabled={disabled}
              required
            />
          </label>
        </div>

        <div className="admin-mkt-lang-block">
          <div className="admin-mkt-lang-tabs" role="tablist">
            {SERVICE_LANGS.map((l) => {
              const filled = !!(form[`title${l.suffix}`] || '').trim();
              return (
                <button
                  key={l.code}
                  type="button"
                  role="tab"
                  aria-selected={lang === l.code}
                  className={`admin-mkt-lang-tab ${lang === l.code ? 'active' : ''}`}
                  onClick={() => setLang(l.code)}
                  disabled={disabled}
                >
                  <span>{t(`admin.marketplace.services.languages.${l.code}`)}</span>
                  <span
                    className={`admin-mkt-lang-dot ${filled ? 'filled' : ''}`}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
          <span className="admin-mkt-hint">{t('admin.marketplace.services.langHint')}</span>

          <label className="admin-promo-field admin-mkt-lang-field">
            <span className="admin-promo-field-label">
              {t('admin.marketplace.services.serviceTitleLang', {
                lang: t(`admin.marketplace.services.languages.${lang}`),
              })}
            </span>
            <input
              className="premium-input-minimal"
              value={form[titleKey] || ''}
              onChange={(e) => setForm((p) => ({ ...p, [titleKey]: e.target.value }))}
              placeholder={t('admin.marketplace.services.titlePlaceholder')}
              disabled={disabled}
            />
          </label>

          <label className="admin-promo-field admin-mkt-desc-field">
            <span className="admin-promo-field-label">
              {t('admin.marketplace.services.descriptionLang', {
                lang: t(`admin.marketplace.services.languages.${lang}`),
              })}{' '}
              <span className="admin-form-optional">{t('admin.form.optional')}</span>
            </span>
            <textarea
              className="premium-input-minimal admin-mkt-desc-input"
              value={form[descKey] || ''}
              onChange={(e) => setForm((p) => ({ ...p, [descKey]: e.target.value }))}
              placeholder={t('admin.marketplace.services.descriptionPlaceholder')}
              rows={3}
              disabled={disabled}
            />
          </label>
        </div>
      </>
    );
  };

  return (
    <div className="admin-promo-codes admin-promo-list-page admin-marketplace">
      <div className="admin-promo-list-shell">
        <header className="admin-promo-list-header">
          <div className="admin-promo-list-header-text">
            <h3 className="admin-promo-list-title">
              <span className="admin-promo-list-title-icon admin-mkt-title-icon">
                <Icon name="shopping-cart" size={18} />
              </span>
              {t('admin.marketplace.title')}
            </h3>
            <p className="admin-promo-list-subtitle">{t('admin.marketplace.subtitle')}</p>
          </div>
          <div className="admin-promo-header-actions">
            {section === 'services' && isDev ? (
              <>
                <button
                  type="button"
                  className="btn-premium-action-new admin-promo-refresh-btn"
                  onClick={loadServices}
                  disabled={servicesLoading}
                  title={t('admin.marketplace.services.refresh')}
                >
                  <Icon name="refresh" size={16} />
                </button>
                <button
                  type="button"
                  className="btn-premium-action-new"
                  onClick={() => {
                    setCreateOpen((v) => !v);
                    setCreateError(null);
                  }}
                >
                  <Icon name={createOpen ? 'x' : 'plus-circle'} size={16} />
                  <span>
                    {createOpen
                      ? t('admin.marketplace.services.cancelCreate')
                      : t('admin.marketplace.services.create')}
                  </span>
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn-premium-action-new admin-promo-refresh-btn"
                onClick={() => loadPurchases(purchasePage, purchasePageSize, purchaseSort, purchaseDir)}
                disabled={purchasesLoading}
                title={t('admin.marketplace.purchases.refresh')}
              >
                <Icon name="refresh" size={16} />
              </button>
            )}
          </div>
        </header>

        {/* Переключатель разделов — сервисы доступны только DEV, поэтому для
            обычного ADMIN переключатель не нужен: сразу показываются покупки. */}
        {isDev && (
          <div className="admin-mkt-tabs">
            <button
              type="button"
              className={`admin-mkt-tab ${section === 'services' ? 'active' : ''}`}
              onClick={() => setSection('services')}
            >
              <Icon name="package" size={16} />
              <span>{t('admin.marketplace.tabs.services')}</span>
            </button>
            <button
              type="button"
              className={`admin-mkt-tab ${section === 'purchases' ? 'active' : ''}`}
              onClick={() => setSection('purchases')}
            >
              <Icon name="bar-chart-2" size={16} />
              <span>{t('admin.marketplace.tabs.purchases')}</span>
              {purchaseTotalElements > 0 && (
                <span className="admin-mkt-tab-count">{purchaseTotalElements}</span>
              )}
            </button>
          </div>
        )}

        <AnimatePresence mode="wait">
          {section === 'services' && isDev ? (
            <motion.div
              key="services"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {createSuccess && (
                <div className="form-status success admin-promo-banner" role="status">
                  {t('admin.marketplace.services.createSuccess')}
                </div>
              )}

              <div
                className={`admin-promo-create-collapse${createOpen ? ' is-open' : ''}`}
                aria-hidden={!createOpen}
              >
                <div className="admin-promo-create-collapse-inner">
                  <form className="admin-promo-create-panel" onSubmit={handleCreateSubmit}>
                    <div className="admin-promo-create-panel-inner">
                      <h4 className="admin-promo-create-panel-title">
                        {t('admin.marketplace.services.create')}
                      </h4>
                      {renderServiceFields(
                        createForm,
                        setCreateForm,
                        createSubmitting,
                        createLang,
                        setCreateLang
                      )}
                      {createError && (
                        <div className="form-status error" role="alert">
                          {createError}
                        </div>
                      )}
                      <div className="admin-promo-create-actions">
                        <button
                          type="submit"
                          className="btn-premium-action-new"
                          disabled={createSubmitting}
                        >
                          <Icon name="check" size={16} />
                          <span>
                            {createSubmitting
                              ? t('admin.marketplace.services.submitting')
                              : t('admin.marketplace.services.submit')}
                          </span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div className="admin-promo-toolbar">
                <span className="admin-promo-results-count">
                  {t('admin.marketplace.services.totalCount', { count: services.length })}
                </span>
              </div>

              <div className="admin-promo-list-body">
                {servicesLoading && (
                  <p className="admin-promo-muted admin-promo-list-empty">{t('common.loading')}</p>
                )}
                {servicesError && !servicesLoading && (
                  <div className="form-status error" role="alert">
                    {servicesError}
                  </div>
                )}
                {!servicesLoading && !servicesError && services.length === 0 && (
                  <div className="admin-promo-empty-state">
                    <Icon name="shopping-cart" size={40} />
                    <p>{t('admin.marketplace.services.empty')}</p>
                    <button
                      type="button"
                      className="btn-premium-action-new"
                      onClick={() => setCreateOpen(true)}
                    >
                      <Icon name="plus-circle" size={16} />
                      <span>{t('admin.marketplace.services.create')}</span>
                    </button>
                  </div>
                )}
                {!servicesLoading && services.length > 0 && (
                  <div className="admin-mkt-cards">
                    {services.map((service) => (
                      <article key={service.id} className="admin-mkt-card">
                        <div className="admin-mkt-card-head">
                          <ServiceIcon value={service.icon} size={48} />
                          <span className="admin-mkt-card-price">
                            {t('admin.marketplace.priceValue', { price: service.price })}
                          </span>
                        </div>
                        <div className="admin-mkt-card-body">
                          <h4 className="admin-mkt-card-title">
                            {localizedField(service, 'title', locale)}
                          </h4>
                          <p className="admin-mkt-card-desc">
                            {localizedField(service, 'description', locale) || (
                              <span className="admin-promo-muted">
                                {t('admin.marketplace.services.noDescription')}
                              </span>
                            )}
                          </p>
                          <div className="admin-mkt-card-meta">
                            <span className="admin-mkt-meta-item">
                              <Icon name="calendar" size={13} />
                              {formatDisplayDate(service.createdAt, locale)}
                            </span>
                          </div>
                        </div>
                        <div className="admin-mkt-card-actions">
                          <button
                            type="button"
                            className="admin-banner-icon-btn"
                            onClick={() => openEdit(service)}
                            title={t('admin.marketplace.services.edit')}
                            aria-label={t('admin.marketplace.services.edit')}
                          >
                            <Icon name="pencil" size={16} />
                          </button>
                          <button
                            type="button"
                            className="admin-banner-icon-btn danger"
                            onClick={() => {
                              setDeleteError(null);
                              setDeleteTarget(service);
                            }}
                            title={t('admin.marketplace.services.delete')}
                            aria-label={t('admin.marketplace.services.delete')}
                          >
                            <Icon name="trash-2" size={16} />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="purchases"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="admin-promo-toolbar admin-mkt-purchases-toolbar admin-mkt-purchases-toolbar--first">
                <span className="admin-promo-results-count">
                  {t('admin.marketplace.purchases.totalCount', { count: purchaseTotalElements })}
                </span>
                <PurchasesSortMenu
                  sort={purchaseSort}
                  direction={purchaseDir}
                  onSortChange={handlePurchaseSortChange}
                  onDirectionChange={handlePurchaseDirChange}
                />
              </div>

              <div className="admin-promo-list-body">
                {purchasesLoading && (
                  <p className="admin-promo-muted admin-promo-list-empty">{t('common.loading')}</p>
                )}
                {purchasesError && !purchasesLoading && (
                  <div className="form-status error" role="alert">
                    {purchasesError}
                  </div>
                )}
                {!purchasesLoading && !purchasesError && purchases.length === 0 && (
                  <div className="admin-promo-empty-state">
                    <Icon name="shopping-cart" size={40} />
                    <p>{t('admin.marketplace.purchases.empty')}</p>
                  </div>
                )}
                {!purchasesLoading && purchases.length > 0 && (
                  <>
                    <div className="admin-promo-table-wrap admin-promo-table-wrap--list">
                      <table className="admin-promo-table admin-promo-table--list admin-mkt-table">
                        <thead>
                          <tr>
                            <th>{t('admin.marketplace.purchases.user')}</th>
                            <th>{t('admin.marketplace.purchases.service')}</th>
                            <th>{t('admin.marketplace.purchases.price')}</th>
                            <th>{t('admin.marketplace.purchases.status')}</th>
                            <th>{t('admin.marketplace.purchases.createdAt')}</th>
                            <th className="admin-mkt-col-actions" aria-hidden="true" />
                          </tr>
                        </thead>
                        <tbody>
                          {purchases.map((purchase) => (
                            <tr key={purchase.id}>
                              <td className="admin-mkt-cell-title">
                                {purchase.userFullName || <span className="admin-promo-muted">—</span>}
                              </td>
                              <td>{purchase.serviceTitle || <span className="admin-promo-muted">—</span>}</td>
                              <td className="admin-mkt-cell-price">
                                {t('admin.marketplace.priceValue', { price: purchase.price })}
                              </td>
                              <td>
                                <span
                                  className={`admin-promo-badge admin-mkt-status admin-mkt-status--${purchaseStatusBadgeClass(
                                    purchase.status
                                  )}`}
                                >
                                  <Icon name={purchaseStatusIcon(purchase.status)} size={13} />
                                  {statusLabel(purchase.status)}
                                </span>
                              </td>
                              <td className="admin-promo-table-date">
                                {formatDisplayDate(purchase.createdAt, locale)}
                              </td>
                              <td className="admin-mkt-col-actions">
                                <button
                                  type="button"
                                  className="admin-banner-icon-btn"
                                  onClick={() => openStatus(purchase)}
                                  title={t('admin.marketplace.purchases.changeStatus')}
                                  aria-label={t('admin.marketplace.purchases.changeStatus')}
                                >
                                  <Icon name="sliders" size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {renderPurchasesPager()}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Модалка редактирования сервиса */}
      {editTarget && editForm && createPortal(
        <div className="admin-modal-backdrop" onClick={closeEdit} role="presentation">
          <div
            className="admin-modal admin-mkt-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mkt-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="mkt-edit-title" className="admin-promo-confirm-title">
                {t('admin.marketplace.services.editTitle')}
              </h3>
            </div>
            <div className="modal-body admin-mkt-edit-body">
              {renderServiceFields(editForm, setEditForm, editSubmitting, editLang, setEditLang)}
              {editError && (
                <div className="form-status error" role="alert">
                  {editError}
                </div>
              )}
            </div>
            <div className="modal-actions admin-modal-actions">
              <button
                type="button"
                className="btn btn-secondary admin-modal-btn-cancel"
                onClick={closeEdit}
                disabled={editSubmitting}
              >
                {t('admin.marketplace.confirm.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary admin-modal-btn-submit"
                onClick={handleEditSubmit}
                disabled={editSubmitting}
              >
                {editSubmitting
                  ? t('admin.marketplace.services.submitting')
                  : t('admin.marketplace.services.saveChanges')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Смена статуса покупки */}
      {statusTarget && createPortal(
        <div className="admin-modal-backdrop" onClick={closeStatus} role="presentation">
          <div
            className="admin-modal admin-mkt-status-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mkt-status-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="mkt-status-title" className="admin-promo-confirm-title">
                {t('admin.marketplace.purchases.changeStatus')}
              </h3>
            </div>
            <div className="modal-body">
              <ul className="confirm-list">
                <li>
                  <strong>{t('admin.marketplace.purchases.user')}:</strong>{' '}
                  {statusTarget.userFullName || '—'}
                </li>
                <li>
                  <strong>{t('admin.marketplace.purchases.service')}:</strong>{' '}
                  {statusTarget.serviceTitle || '—'}
                </li>
              </ul>
              <div className="admin-promo-field" style={{ marginTop: 12 }}>
                <span className="admin-promo-field-label">
                  {t('admin.marketplace.purchases.newStatus')}
                </span>
                <PremiumSelect
                  value={statusValue}
                  onChange={setStatusValue}
                  options={PURCHASE_STATUSES.map((s) => ({
                    value: s,
                    label: statusLabel(s),
                    icon: purchaseStatusIcon(s),
                  }))}
                />
              </div>
              {statusError && (
                <div className="form-status error" role="alert" style={{ marginTop: 12 }}>
                  {statusError}
                </div>
              )}
            </div>
            <div className="modal-actions admin-modal-actions">
              <button
                type="button"
                className="btn btn-secondary admin-modal-btn-cancel"
                onClick={closeStatus}
                disabled={statusSubmitting}
              >
                {t('admin.marketplace.confirm.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary admin-modal-btn-submit"
                onClick={handleConfirmStatus}
                disabled={statusSubmitting || !statusValue || statusValue === statusTarget.status}
              >
                {statusSubmitting
                  ? t('admin.marketplace.services.submitting')
                  : t('admin.marketplace.purchases.saveStatus')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Подтверждение удаления сервиса */}
      <PromoConfirmModal
        open={!!deleteTarget}
        title={t('admin.marketplace.confirm.deleteTitle')}
        subtitle={t('admin.marketplace.confirm.deleteSubtitle')}
        items={
          deleteTarget
            ? [
                {
                  label: t('admin.marketplace.services.serviceTitle'),
                  value: localizedField(deleteTarget, 'title', locale),
                },
                {
                  label: t('admin.marketplace.services.price'),
                  value: t('admin.marketplace.priceValue', { price: deleteTarget.price }),
                },
              ]
            : []
        }
        error={deleteError}
        submitting={deleteSubmitting}
        onCancel={() => {
          if (deleteSubmitting) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
        cancelLabel={t('admin.marketplace.confirm.cancel')}
        confirmLabel={
          deleteSubmitting
            ? t('admin.marketplace.services.deleting')
            : t('admin.marketplace.confirm.delete')
        }
        titleId="mkt-delete-confirm-title"
      />
    </div>
  );
};

export default AdminMarketplace;
